import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';

export interface Settings {
  defaultTimezone: string;
  defaultCountry: string;
  defaultCurrency?: string;
  store_payment_options?: any;
}

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  // North America
  CA: 'CAD', US: 'USD', MX: 'MXN',
  // Caribbean
  TT: 'TTD', JM: 'JMD', BB: 'BBD', GY: 'GYD', BS: 'BSD', SR: 'SRD',
  LC: 'XCD', VC: 'XCD', GD: 'XCD', AG: 'XCD', DM: 'XCD', KN: 'XCD',
  TC: 'USD', KY: 'KYD', BM: 'BMD', AW: 'AWG', CW: 'ANG', SX: 'ANG',
  VG: 'USD', VI: 'USD', PR: 'USD', CU: 'CUP', DO: 'DOP', HT: 'HTG',
  // Central America
  GT: 'GTQ', BZ: 'BZD', HN: 'HNL', SV: 'USD', NI: 'NIO', CR: 'CRC', PA: 'PAB',
  // South America
  AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES', EC: 'USD',
  BO: 'BOB', PY: 'PYG', UY: 'UYU', BR: 'BRL',
  // Western Europe
  GB: 'GBP', IE: 'EUR', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR',
  PT: 'EUR', NL: 'EUR', BE: 'EUR', LU: 'EUR', AT: 'EUR', FI: 'EUR',
  GR: 'EUR', CY: 'EUR', MT: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR',
  LV: 'EUR', LT: 'EUR', HR: 'EUR', CH: 'CHF', NO: 'NOK', SE: 'SEK',
  DK: 'DKK', IS: 'ISK',
  // Central/Eastern Europe
  PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD',
  BA: 'BAM', MK: 'MKD', AL: 'ALL', ME: 'EUR', XK: 'EUR', MD: 'MDL',
  UA: 'UAH', BY: 'BYN', RU: 'RUB', GE: 'GEL', AM: 'AMD', AZ: 'AZN',
  // Middle East & North Africa
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  JO: 'JOD', IQ: 'IQD', IR: 'IRR', IL: 'ILS', LB: 'LBP', SY: 'SYP',
  YE: 'YER', EG: 'EGP', LY: 'LYD', TN: 'TND', DZ: 'DZD', MA: 'MAD',
  SD: 'SDG', ET: 'ETB',
  // Sub-Saharan Africa
  NG: 'NGN', GH: 'GHS', KE: 'KES', TZ: 'TZS', UG: 'UGX', ZA: 'ZAR',
  ZM: 'ZMW', ZW: 'ZWL', BW: 'BWP', NA: 'NAD', MZ: 'MZN', AO: 'AOA',
  CD: 'CDF', CM: 'XAF', SN: 'XOF', CI: 'XOF', ML: 'XOF', BF: 'XOF',
  NE: 'XOF', TG: 'XOF', BJ: 'XOF', GN: 'GNF', SL: 'SLL', LR: 'LRD',
  GM: 'GMD', MR: 'MRU', MG: 'MGA', MU: 'MUR', SC: 'SCR', RW: 'RWF',
  BI: 'BIF', SO: 'SOS', DJ: 'DJF', ER: 'ERN', SS: 'SSP', CF: 'XAF',
  TD: 'XAF', GA: 'XAF', CG: 'XAF', GQ: 'XAF', ST: 'STN', CV: 'CVE',
  // South & East Asia
  IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', BT: 'BTN',
  MV: 'MVR', AF: 'AFN', CN: 'CNY', JP: 'JPY', KR: 'KRW', KP: 'KPW',
  TW: 'TWD', HK: 'HKD', MO: 'MOP', MN: 'MNT', TH: 'THB', VN: 'VND',
  MY: 'MYR', SG: 'SGD', ID: 'IDR', PH: 'PHP', MM: 'MMK', KH: 'KHR',
  LA: 'LAK', TL: 'USD', BN: 'BND',
  // Central Asia
  KZ: 'KZT', UZ: 'UZS', TM: 'TMT', KG: 'KGS', TJ: 'TJS',
  // Oceania
  AU: 'AUD', NZ: 'NZD', FJ: 'FJD', PG: 'PGK', SB: 'SBD', VU: 'VUV',
  WS: 'WST', TO: 'TOP', FM: 'USD', PW: 'USD', MH: 'USD', NR: 'AUD',
  TV: 'AUD', KI: 'AUD', CK: 'NZD', NU: 'NZD',
  // Turkey & Misc
  TR: 'TRY',
};

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private cachedSettings: Settings | null = null;
  private loadInFlight: Observable<{ ok: boolean; settings: Settings }> | null = null;

  constructor(private apiService: ApiService) {}

  getSettings(): Observable<{ ok: boolean; settings: Settings }> {
    return this.apiService.getAuth(environment.BASEURL + environment.SETTINGS_GET_APIURL, '');
  }

  loadSettings(): Observable<{ ok: boolean; settings: Settings }> {
    return this.getSettings().pipe(
      tap(res => { if (res.ok) this.cachedSettings = res.settings; })
    );
  }

  /**
   * Lazy boot: first consumer triggers GET /settings; subsequent calls reuse cache / in-flight.
   * Keeps homepage cold load free of the settings request.
   */
  ensureLoaded(): Observable<{ ok: boolean; settings: Settings }> {
    if (this.cachedSettings) {
      return of({ ok: true, settings: this.cachedSettings });
    }
    if (!this.loadInFlight) {
      this.loadInFlight = this.loadSettings().pipe(
        tap({
          error: () => {
            this.loadInFlight = null;
          },
        }),
        shareReplay(1)
      );
    }
    return this.loadInFlight;
  }

  updateSettings(data: Partial<Settings>): Observable<{ ok: boolean; settings: Settings }> {
    return this.apiService.put(environment.BASEURL + environment.SETTINGS_GET_APIURL, data);
  }

  getCurrencyByCountry(country: string): string {
    return COUNTRY_CURRENCY_MAP[country] || 'CAD';
  }

  getCurrency(settings: Settings): string {
    if (settings.defaultCurrency) return settings.defaultCurrency;
    return this.getCurrencyByCountry(settings.defaultCountry);
  }

  getActiveCurrency(): string {
    this.kickEnsureLoaded();
    return this.cachedSettings ? this.getCurrency(this.cachedSettings) : 'CAD';
  }

  getDefaultCountry(): string | null {
    this.kickEnsureLoaded();
    return this.cachedSettings?.defaultCountry ?? null;
  }

  private kickEnsureLoaded(): void {
    if (this.cachedSettings) {
      return;
    }
    this.ensureLoaded().subscribe({ error: () => { /* defaults until settings load */ } });
  }
}
